import React from 'react'
import { useSchedule } from '../../context/ScheduleContext'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import { DayType } from '../constants/DayType'
import ScheduleContextMenuOption from './ScheduleContextMenuOption'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'
import {
  faArrowUpRightFromSquareSolid,
  FaIcon,
  faRotateReverseSolid,
  faMagnifyingGlassMinusSolid,
} from '@/lib/fa'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { getDayTypeName } from '../helpers/TimesheetDayType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import ScheduleContextMenuSearchBar from './ScheduleContextMenuSearchBar'
import { useDayEvents } from '@/modules/drivers/hooks/useDayEvents'
import useCalendarService from '@/modules/drivers/services/CalendarService'

import { MenuItem, MenuDivider, ControlledMenu } from '@szhsin/react-menu'
import '@szhsin/react-menu/dist/index.css'
import FlexView from '@/modules/shared/components/FlexView'

type ScheduleContextMenuProps = {
  isOpen: boolean
  position: { x: number; y: number }
  onClose: () => void
  driverId: number
  companyId: string
  isPinnedLoading: boolean
  pinnedDays: DayType[]
  fetchPinnedDays: () => void
  setIsOpenDayPlanModal: () => void
}

const ScheduleContextMenu: React.FC<ScheduleContextMenuProps> = ({
  isOpen,
  position,
  onClose,
  driverId,
  companyId,
  isPinnedLoading,
  pinnedDays,
  fetchPinnedDays,
  setIsOpenDayPlanModal,
}) => {
  const {
    selectedDays,
    setIsOpenDayTypeModal,
    isOpenDayTypeModal,
    setIsLoading,
    setSelectedDays,
    refresh: refreshData,
  } = useSchedule()
  const { handleRestoreDefaultDayType } = useDayEvents()
  const { changeDayTypes } = useTimesheetService()
  const { changeDayTypes: changeDayDetailsCalendar } = useCalendarService()

  const { t } = useLocalize()
  const [searchQuery, setSearchQuery] = React.useState('')
  const [minHeight, setMinHeight] = React.useState<number | null>(null)
  const [showDayTypePanel, setShowDayTypePanel] = React.useState(false)
  const transitioningRef = React.useRef(false)
  const contentRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      if (!showDayTypePanel) {
        setShowDayTypePanel(false)
      }
    }
  }, [isOpen])

  const changeDayType = async (type: number) => {
    setIsLoading(true)
    try {
      await changeDayTypes({
        companyId: companyId,
        driverId: driverId,
        dates: selectedDays,
        dayType: type,
        notes: null,
        // Jak w WPF: urlop w sobotę dostaje 08:00 i 8 h.
        applyScheduleHours: true,
      })
    } catch (error) {
      console.error('Error changing day type:', error)
    } finally {
      setIsLoading(false)
      refreshData()
      setSelectedDays([])
      onClose()
    }
  }
  const restoreDefaultDayData = async () => {
    setIsLoading(true)
    try {
      await handleRestoreDefaultDayType(
        companyId,
        driverId,
        selectedDays,
        changeDayDetailsCalendar,
      )
    } catch (error) {
      console.error('Error resotring default day type:', error)
    } finally {
      setIsLoading(false)
      refreshData()
      setSelectedDays([])
      onClose()
    }
  }
  const allDayTypes = Object.values(DayType)
    .filter((value) => typeof value === 'number' && value !== DayType.NONE)
    .map((value) => value as DayType)

  React.useEffect(() => {
    if (contentRef.current && !minHeight) {
      const height = contentRef.current.clientHeight
      setMinHeight(height)
    }
  }, [minHeight])

  const filteredDayTypes = React.useMemo(() => {
    if (!searchQuery) return pinnedDays

    return allDayTypes.filter((dayType) => {
      const dayTypeName = getDayTypeName(dayType, t).toLowerCase()
      const dayTypeCode = DayType[dayType].toLowerCase()
      const query = searchQuery.toLowerCase()
      return dayTypeName.includes(query) || dayTypeCode.includes(query)
    })
  }, [searchQuery, allDayTypes, pinnedDays, t])

  if (!isOpen) return null

  return (
    <>
      {/* First-level context menu */}
      {!showDayTypePanel && (
        <ControlledMenu
          anchorPoint={position}
          state={isOpen ? 'open' : 'closed'}
          onClose={(e) => {
            if (!transitioningRef.current) {
              onClose()
            }
            transitioningRef.current = false
          }}
          className="tw-z-[10000]"
          portal={true}
          onContextMenu={(e) => e.preventDefault()}
        >
          <MenuItem
            className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1 tw-text-sm tw-text-content-base"
            onClick={() => {
              transitioningRef.current = true
              setShowDayTypePanel(true)
            }}
          >
            <FlexView
              hAlignContent="left"
              vAlignContent="center"
              className="tw-gap-2"
            >
              <span>Zmień typ dnia</span>
            </FlexView>
          </MenuItem>
          <MenuDivider className="tw-m-0 tw-p-0" />
          <MenuItem
            className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1 tw-text-sm tw-text-content-base"
            onClick={() => {
              setIsOpenDayPlanModal()
              onClose()
            }}
          >
            <FlexView
              hAlignContent="left"
              vAlignContent="center"
              className="tw-gap-2"
            >
              <span>Zmień wartości</span>
            </FlexView>
          </MenuItem>
        </ControlledMenu>
      )}

      {/* Day type panel */}
      {showDayTypePanel && (
        <>
          <div
            className="tw-fixed tw-inset-0 tw-z-[9998]"
            onClick={() => {
              setShowDayTypePanel(false)
              onClose()
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              setShowDayTypePanel(false)
              onClose()
            }}
          />
          <div
            className={`tw-fixed tw-z-[9999] ${isOpenDayTypeModal ? 'tw-hidden' : ''}`}
            style={{ top: position.y, left: position.x }}
          >
            <div
              ref={contentRef}
              className={`tw-flex tw-max-h-[500px] tw-w-[380px] tw-flex-col tw-space-y-1 tw-rounded-lg tw-bg-white tw-p-2 tw-px-1.5 tw-shadow-lg`}
              style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
            >
              <ScheduleContextMenuSearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
              {!searchQuery && (
                <span className="tw-mb-1.5 tw-mt-1 tw-px-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base">
                  Przypięte
                </span>
              )}
              <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-overflow-y-auto tw-pr-1">
                {isPinnedLoading && !searchQuery ? (
                  <div className="tw-flex tw-h-[300px] tw-items-center tw-justify-center">
                    <LoaderIcon className="tw-h-6 tw-w-6 tw-animate-spin tw-text-primary" />
                  </div>
                ) : filteredDayTypes.length > 0 ? (
                  filteredDayTypes.map((dayType) => (
                    <ScheduleContextMenuOption
                      key={dayType}
                      dayType={dayType}
                      changeDayType={changeDayType}
                      companyId={companyId}
                      searchQuery={searchQuery}
                      isPinned={pinnedDays.includes(dayType)}
                      onPinToggle={fetchPinnedDays}
                    />
                  ))
                ) : (
                  <div className="tw-flex tw-h-[300px] tw-flex-col tw-items-center tw-justify-center tw-gap-4">
                    <FaIcon
                      icon={faMagnifyingGlassMinusSolid}
                      className="tw-h-10 tw-w-10 tw-text-[#D1E2F6]"
                    />
                    <span className="tw-text-xs tw-text-content-base/80">
                      {!searchQuery
                        ? 'Brak przypiętych typów dni'
                        : 'Nie znaleziono typów dni'}
                    </span>
                  </div>
                )}
              </div>
              {!searchQuery && (
                <>
                  <DividerHorizontal />
                  <div
                    onClick={() => restoreDefaultDayData()}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded tw-py-1 hover:tw-bg-[#F1F4FF]"
                  >
                    <FaIcon
                      icon={faRotateReverseSolid}
                      className="tw-h-3 tw-w-3 tw-text-primary"
                    />
                    <span className="tw-text-sm tw-font-normal tw-text-primary">
                      Przywróć domyślny typ dnia
                    </span>
                  </div>
                  <DividerHorizontal />
                  <div
                    onClick={() => setIsOpenDayTypeModal(true)}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded tw-py-1 hover:tw-bg-[#F1F4FF]"
                  >
                    <span className="tw-text-sm tw-font-normal tw-text-content-base">
                      Wszystkie typy dnia
                    </span>
                    <FaIcon
                      icon={faArrowUpRightFromSquareSolid}
                      className="tw-h-3 tw-w-3 tw-text-content-base"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default ScheduleContextMenu
